import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Database,
  ShieldCheck,
  Workflow,
  Target,
  Check,
  ChevronDown,
} from "lucide-react";
import {
  Hero,
  BookingCTA,
  PipelineDiagram,
  SectionHeader,
} from "../components/sections";
import { Comparison } from "../components/sections/Comparison";

const comparison = [
  {
    dimension: "Finding the right buyers",
    traditional: "Hours of searching and scattered spreadsheets.",
    uniqreach:
      "A defined ICP, connected data sources, and focused prospect discovery.",
  },
  {
    dimension: "Making outreach relevant",
    traditional: "Generic messages with a name swapped in.",
    uniqreach: "AI research and verified context that inform each message.",
  },
  {
    dimension: "Keeping work moving",
    traditional: "Manual follow-ups and disconnected tools.",
    uniqreach: "Connected sequences, response handling, and CRM workflows.",
  },
  {
    dimension: "Knowing what to improve",
    traditional: "Activity reports after a campaign ends.",
    uniqreach: "Ongoing reviews of conversations, data quality, and pipeline.",
  },
];
const capabilities = [
  {
    icon: Database,
    number: "01",
    title: "Find the right people.",
    description:
      "Discover, enrich, and verify prospects against your ideal customer profile. Better outreach starts with better data.",
    href: "/technology/data-prospecting",
    link: "Data & prospecting",
  },
  {
    icon: Brain,
    number: "02",
    title: "Make every touch relevant.",
    description:
      "Use AI to research accounts, understand buyer context, and support thoughtful personalization. Your team sets the direction.",
    href: "/technology/ai-automation",
    link: "AI & automation",
  },
  {
    icon: ShieldCheck,
    number: "03",
    title: "Build on a solid foundation.",
    description:
      "Connect sending infrastructure, follow-up logic, and your CRM. Monitor the system and improve it as you learn.",
    href: "/technology/outbound-infrastructure",
    link: "Outbound infrastructure",
  },
];
const questions = [
  {
    question: "What does UniqReach actually build?",
    answer:
      "An outbound sales system built around your business. It can connect prospect discovery, verified data, AI research, personalized outreach, follow-ups, response handling, and CRM workflows. The scope depends on your goals and existing tools.",
  },
  {
    question: "Is this a fit for our company?",
    answer:
      "We focus on B2B technology companies with a clear offering and identifiable buyers, including SaaS, AI, software, IT services, and technology consultancies. The strategy call helps establish whether an outbound system makes sense for your market and sales process.",
  },
  {
    question: "Will AI replace our sales team?",
    answer:
      "AI supports research, personalization, and repetitive workflow decisions. People remain responsible for strategy, quality, relationships, and closing. The goal is to give your sales team more time for valuable conversations.",
  },
  {
    question: "Do you guarantee meetings or revenue?",
    answer:
      "No. Outcomes depend on your market, offer, positioning, and sales execution. We agree on a clear scope and success measures, then review performance and iterate together.",
  },
];

export default function HomePage() {
  return (
    <>
      <Hero />
      <section
        className="audience-strip"
        aria-label="Built for B2B technology businesses"
      >
        <div className="container audience-inner">
          <span className="label">Built for ambitious B2B teams</span>
          <div>
            <span>SaaS</span>
            <span>AI & software</span>
            <span>IT services</span>
            <span>Tech consultancies</span>
          </div>
        </div>
      </section>
      <section className="section" aria-labelledby="opportunity-title">
        <div className="container editorial-grid">
          <SectionHeader
            id="opportunity-title"
            eyebrow="Give your team its time back"
            title="Less busywork. More meaningful conversations."
            description="Your best sales hours belong with customers. We connect the work that happens before a conversation, so your team can focus on what happens next."
          />
          <div className="work-shift">
            <dl className="system-counts" aria-label="System structure">
              <div>
                <dt>Connected stages</dt>
                <dd>06</dd>
              </div>
              <div>
                <dt>Building blocks</dt>
                <dd>03</dd>
              </div>
              <div>
                <dt>Sales system</dt>
                <dd>01</dd>
              </div>
            </dl>
            <div className="work-shift-label">
              <Workflow size={20} aria-hidden="true" />
              <span>The work behind your next opportunity</span>
            </div>
            {[
              "Finding companies that fit",
              "Researching and verifying contacts",
              "Personalizing outreach and following up",
              "Qualifying responses and updating your CRM",
            ].map((text, index) => (
              <div className="work-shift-row" key={text}>
                <span className="step-number">0{index + 1}</span>
                <span>{text}</span>
                <Check size={18} aria-hidden="true" />
              </div>
            ))}
            <p>Connected in one system. Guided by human expertise.</p>
          </div>
        </div>
      </section>
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="capabilities-title"
      >
        <div className="container">
          <SectionHeader
            id="capabilities-title"
            eyebrow="The building blocks"
            title="A complete engine. Built around your business."
            description="Data, intelligence, and infrastructure working together, from first prospect to qualified conversation."
          />
          <div className="capability-grid">
            {capabilities.map((item) => (
              <Link
                to={item.href}
                key={item.title}
                className="capability-card card feature-link"
              >
                <div className="capability-top">
                  <item.icon size={28} aria-hidden="true" />
                  <span className="step-number">{item.number}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <span className="text-link">
                  {item.link}
                  <ArrowRight size={18} aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="section" aria-labelledby="comparison-title">
        <div className="container">
          <SectionHeader
            id="comparison-title"
            eyebrow="A more connected approach"
            title="Move from scattered activity to a repeatable system."
            description="See what changes when your tools, data, and sales process work together."
          />
          <Comparison rows={comparison} />
          <Link to="/why-uniqreach" className="text-link mt-8">
            Why UniqReach
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </section>
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="process-title"
      >
        <div className="container">
          <div className="section-heading-row">
            <SectionHeader
              id="process-title"
              eyebrow="How it works"
              title="From the right market to the next conversation."
              description="Six connected stages, with quality checks and human decisions along the way."
            />
            <Link to="/how-it-works" className="text-link">
              Explore the process
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
          <PipelineDiagram />
        </div>
      </section>
      <section className="section" aria-labelledby="oversight-title">
        <div className="container editorial-grid">
          <div className="oversight-panel card-elevated">
            <span className="label">A clear division of work</span>
            <div className="oversight-row">
              <Brain size={24} aria-hidden="true" />
              <div>
                <h3>AI finds the context.</h3>
                <p>Research, classification, and personalization.</p>
              </div>
            </div>
            <div className="oversight-row">
              <Workflow size={24} aria-hidden="true" />
              <div>
                <h3>Automation connects the work.</h3>
                <p>Follow-ups, response routing, and CRM updates.</p>
              </div>
            </div>
            <div className="oversight-row">
              <Target size={24} aria-hidden="true" />
              <div>
                <h3>People make the decisions.</h3>
                <p>Strategy, quality control, and relationships.</p>
              </div>
            </div>
          </div>
          <div>
            <SectionHeader
              id="oversight-title"
              eyebrow="Intelligence with intention"
              title="Powered by AI. Accountable to people."
              description="Automation should make your sales process more useful, not harder to understand. We explain what runs, where people review it, and how performance is measured."
            />
            <Link to="/technology/ai-automation" className="text-link mt-8">
              See how we use AI
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="start-title"
      >
        <div className="container">
          <SectionHeader
            id="start-title"
            eyebrow="A considered starting point"
            title="First, understand the opportunity."
            description="A practical conversation about your market, your sales process, and what a better system could look like."
          />
          <ol className="engagement-steps">
            {[
              {
                title: "Share your goals",
                text: "Tell us about your company, buyers, and current outbound process.",
              },
              {
                title: "Explore the fit",
                text: "Review where data, AI, and automation could create useful leverage.",
              },
              {
                title: "Map the system",
                text: "If there is a fit, agree on the scope, priorities, and success measures.",
              },
            ].map((step, index) => (
              <li key={step.title}>
                <span className="step-number">0{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <section className="section" aria-labelledby="questions-title">
        <div className="container editorial-grid faq-home">
          <div>
            <SectionHeader
              id="questions-title"
              eyebrow="Before we talk"
              title="Good questions. Clear answers."
              description="A little more clarity on the system and what you can expect."
            />
            <Link to="/faq" className="text-link mt-8">
              View all FAQs
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
          <div className="faq-list">
            {questions.map((item) => (
              <details key={item.question}>
                <summary>
                  {item.question}
                  <ChevronDown size={20} aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      <BookingCTA />
    </>
  );
}
